<script>
	import Vue from "vue";
	import { mapState, mapGetters } from "vuex";
	import SearchInput from "./Search";

	export default {
		components: { SearchInput },
		computed: {
			...mapGetters(["totals", "getTableIds", "currency", "currencySpacing"]),
			...mapState({
				appId: state => state.app.id,
				contactInfos: state => state.contacts.contactInfos,
				downpayment: state => state.downpayment,
				background: state => state.settings.accentColor.background,
				foreground: state => state.settings.accentColor.foreground,
				isEditable: state => state.home.isEditable,
				vatName: state => state.vat.vatName,
				third_account: state => state.contacts.thirdAccount
			}),
			unitSpacing() {
				return {
					"padding-right": `${this.currencySpacing * 6}px`
				};
			},
			filteredInvoices() {
				return this.downpayment?.invoices.filter(
					invoice => !Object.values(this.table).find(row => row.number === invoice.number)
				);
			},
			table: {
				get() {
					return this.$store.state.downpayment.table;
				},
				set(value) {
					this.$store.commit("downpaymentTable", value);
				}
			}
		},
		watch: {
			totals: {
				handler() {
					this.buildDownpaymentObject();
				},
				deep: true
			},
			table: {
				handler() {
					this.buildDownpaymentObject();
				},
				deep: true,
				immediate: true
			},
			contactInfos: {
				handler() {
					this.$store.dispatch("invoices", {
						appId: this.appId,
						clientId: this.contactInfos?.id,
						type: this.contactInfos?.type
					});
				},
				immediate: true
			},
			third_account(newVal, oldValue) {
				if (newVal !== oldValue) {
					this.$store.dispatch("invoices", {
						appId: this.appId,
						clientId: this.third_account?.id ? this.third_account.id : this.contactInfos?.id,
						type: this.third_account?.type ? this.third_account.type : this.contactInfos?.type
					});
				}
			}
		},
		created() {
			if (Object.keys(this.table).length === 0) {
				this.$store.commit("createRow", null);
			}
		},
		methods: {
			selectedInput({ id, row }) {
				this.$store.commit("createRow", { id, row });
				this.$store.commit("createRow", null);
			},
			removeTable() {
				this.$emit("event-removeDownpaymentTable");
				this.table = {};
				this.buildDownpaymentObject();
			},
			removeEntry(id) {
				Vue.delete(this.table, id);

				if (Object.keys(this.table).length === 0) {
					this.removeTable();
				}
			},
			buildDownpaymentObject() {
				let exclTax = 0;
				let vat = 0;
				let inclTax = 0;

				Object.values(this.table).forEach(row => {
					const { totals, totals_foreign_currency } = row.data ?? {};

					let _exclTax = 0;
					let _vat = 0;
					let _inclTax = 0;

					if (
						totals_foreign_currency?.subtotal?.total != null
						&& totals_foreign_currency?.taxes?.total != null
						&& totals_foreign_currency?.due != null
					) {
						_exclTax = totals_foreign_currency.subtotal.total;
						_vat = totals_foreign_currency.taxes.total;
						_inclTax = totals_foreign_currency.due;

						exclTax += _exclTax;
						vat += _vat;
						inclTax += _inclTax;
					} else if (totals) {
						_exclTax = totals.subtotal.total;
						_vat = totals.taxes.total;
						_inclTax = totals.due;

						exclTax += _exclTax;
						vat += _vat;
						inclTax += _inclTax;
					}
				});

				const downpayment = {
					table: this.table,
					totals: {
						exclTax,
						vat,
						inclTax
					}
				};

				this.$store.commit("downpayment", downpayment);
			}
		}
	};
</script>

<template>
	<div class="position-relative">
		<div v-if="!isEditable" class="disable-table" />

		<b-table-simple small fixed class="billing__table">
			<b-thead class="billing__head" :style="{ 'background-color': background, color: foreground }">
				<b-tr>
					<b-th class="billing__text">
						{{ $t("Acomptes") }}
					</b-th>
					<b-th class="billing__text">
						{{ $t("HT") }}
					</b-th>
					<b-th class="billing__text">
						{{ $t(vatName) }}
					</b-th>
					<b-th class="billing__text">
						{{ $t("TTC") }}
					</b-th>
				</b-tr>
			</b-thead>
			<b-tbody>
				<!-- Disabled first row -->
				<b-tr>
					<b-td class="billing__cell-readonly text-right">
						{{ $t("Total") }}
					</b-td>
					<b-td class="billing__cell billing__cell-readonly unit" :data-unit="currency" :style="unitSpacing">
						<b-form-input
							readonly="readonly"
							:value="totals && totals.table.exclTax | convertNumberToString"
							class="billing__input text-right"
							placeholder="0,00"
						/>
					</b-td>
					<b-td class="billing__cell billing__cell-readonly unit" :data-unit="currency" :style="unitSpacing">
						<b-form-input
							readonly="readonly"
							:value="totals && totals.table.vat | convertNumberToString"
							class="billing__input text-right"
							placeholder="0,00"
						/>
					</b-td>
					<b-td class="billing__cell billing__cell-readonly unit" :data-unit="currency" :style="unitSpacing">
						<b-form-input
							readonly="readonly"
							:value="totals && totals.table.inclTax | convertNumberToString"
							class="billing__input text-right"
							placeholder="0,00"
						/>
					</b-td>
				</b-tr>
				<!-- Down payment invoice row -->
				<b-tr v-for="({ data }, index) in table" :key="index">
					<b-td class="billing__cell ">
						<SearchInput
							:id="index"
							:value="data && data.number"
							:placeholder="$t('Numéro facture d’acompte')"
							:data="filteredInvoices"
							field="number"
							@event-selected="selectedInput"
							@event-removeEntry="removeEntry"
						/>
					</b-td>
					<b-td class="billing__cell billing__cell-readonly unit" :data-unit="currency" :style="unitSpacing">
						<b-form-input
							readonly="readonly"
							:value="(data
								? (data.foreign_currency?.length ? data.totals_foreign_currency?.subtotal?.total ?? 0 : data.amount) / 100
								: 0)
								| convertNumberToString"
							class="billing__input text-right"
							placeholder="0,00"
						/>
					</b-td>
					<b-td class="billing__cell billing__cell-readonly unit" :data-unit="currency" :style="unitSpacing">
						<b-form-input
							readonly="readonly"
							:value="(data
								? (data.foreign_currency?.length ? data.totals_foreign_currency?.taxes?.total ?? 0 : data.amount_tax) / 100
								: 0)
								| convertNumberToString"
							class="billing__input text-right"
							placeholder="0,00"
						/>
					</b-td>
					<b-td class="billing__cell billing__cell-readonly unit" :data-unit="currency" :style="unitSpacing">
						<b-form-input
							readonly="readonly"
							:value="(data
								? (data.foreign_currency?.length ? (data.totals_foreign_currency?.subtotal?.total ?? 0) + (data.totals_foreign_currency?.taxes?.total ?? 0) : data.amount + data.amount_tax) / 100
								: 0)
								| convertNumberToString"
							class="billing__input text-right"
							placeholder="0,00"
						/>
					</b-td>
				</b-tr>
			</b-tbody>
		</b-table-simple>
		<b-icon icon="x" class="remove-table" @click="removeTable" />
	</div>
</template>

<style scoped>
	.b-icon.bi.remove-table {
		position: absolute;
		top: 2px;
		right: 0px;
		font-size: 1.7rem;
		color: var(--white);
		cursor: pointer;
	}

	.billing__head .billing__text:first-child {
		width: 27%;
	}
</style>
